
import React, { useState } from 'react';
import { SectionWrapper } from './SectionWrapper';
import { ASSETS } from '../constants';
import { BookOpen, Heart, Users, X, CalendarDays, Clock3 } from 'lucide-react';

interface Workshop {
  id: string;
  engLabel: string;
  title: string;
  subtitle?: string;
  type: string;
  status: 'planning' | 'full' | 'recruiting';
  statusLabel: string;
  date: string; 
  summary: string;
  details: string;
  outline?: string[];
  facilitator: string;
  fee: string;
  schedule: string;
  icon: React.ReactNode;
  colorClass: string;
  textClass: string;
  accentBg: string; // 用作副券区域的背景色
}

const WORKSHOPS: Workshop[] = [
  {
    id: 'w1',
    engLabel: 'KLEINIAN STUDY',
    title: '克莱因学派读书会',
    subtitle: '探索潜意识的幻想与修复',
    type: 'Reading Group',
    status: 'planning',
    statusLabel: '筹备中',
    date: '2025.10.15 开读 | 每周二晚',
    summary: '精读《嫉妒与感恩》，深入克莱因学派的核心领地。在破碎中寻找整合，在嫉妒中发现感恩。',
    details: `【关于读书会】\n克莱因（Melanie Klein）的理论不仅是心理学的巅峰，更是关于人类情感最原始、最深刻的洞察。我们将一起探讨：\n- 偏执分裂位向抑郁位的转变\n- 投射性认同在关系中的运作\n- 修复（Restoration）的力量\n\n【你将获得】\n1. 极具深度的动力学视角。\n2. 识别并整合内在的“好坏客体”。\n3. 共同进修的专业陪伴。`,
    outline: ['婴儿的幻想世界：内部客体', '投射与内化的舞动', '嫉妒的根源及其影响', '通往感恩与修复之路'],
    facilitator: '陈瑞雪 (动力学取向咨询师)',
    fee: '待定',
    schedule: '具体时间待定',
    icon: <BookOpen size={20} />,
    colorClass: 'bg-sage-600',
    textClass: 'text-sage-600',
    accentBg: 'bg-sage-50/80'
  },
  {
    id: 'w2',
    engLabel: 'WOMEN GROWTH',
    title: '女性成长动力团体',
    subtitle: '找回失落的真实声音',
    type: 'Growth Group',
    status: 'planning',
    statusLabel: '筹备中',
    date: '2025.11.02 开组 | 隔周六下午',
    summary: '一个全女性的抱持性空间。我们将讨论身份焦虑、母女联结与自我实现。',
    details: `这是一个封闭式的心理动力学团体。在这里，你不必是完美的妻子、母亲或女儿，你只需要是你自己。我们将利用团体的动力，看见那些被社会文化所压抑的生命能量。`,
    facilitator: '陈瑞雪 & 特邀协管',
    fee: '待定',
    schedule: '具体时间待定',
    icon: <Heart size={20} />,
    colorClass: 'bg-amber-500',
    textClass: 'text-amber-600',
    accentBg: 'bg-amber-50/80'
  },
  {
    id: 'w3',
    engLabel: 'PSYCHO DRAMA',
    title: '心理剧本杀',
    subtitle: '在戏剧中照见真实人生',
    type: 'Psychodrama',
    status: 'full',
    statusLabel: '已截止',
    date: '2025.09.28 | 已结课',
    summary: '借由角色扮演与心理剧技术，在“虚拟”的剧本中探索“真实”的人际互动模式。',
    details: `你选择的角色，往往是你潜意识的投射。我们将在专业的复盘中，剥离剧情，探讨这些角色互动如何折射出你在现实生活中被防御与渴望。这是一场深度的自我探索实验。`,
    facilitator: '咨询室资深团队',
    fee: '待定',
    schedule: '具体时间待定',
    icon: <Users size={20} />,
    colorClass: 'bg-stone-400',
    textClass: 'text-stone-500',
    accentBg: 'bg-stone-100/80'
  }
];

export const Workshops: React.FC = () => {
  const [activeWorkshop, setActiveWorkshop] = useState<Workshop | null>(null);

  return (
    <>
      <SectionWrapper>
        <div className="text-center mb-16 md:mb-28 animate-fade-in flex flex-col items-center">
          <h2 className="font-serif text-3xl md:text-5xl text-stone-900 tracking-widest mb-4 font-black">
            活动工坊
          </h2>
          <div className="flex flex-col items-center gap-2">
            <span className="text-stone-400 font-sans uppercase text-xs tracking-[0.6em] font-bold">The Encounter</span>
            <p className="text-stone-600 font-serif text-xl mt-10 max-w-xl leading-loose italic">
              “在你的剧本里，上演什么样的剧目”
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-16 mb-12 max-w-4xl mx-auto px-4 md:px-0">
          {WORKSHOPS.map((workshop) => (
            <div key={workshop.id} onClick={() => setActiveWorkshop(workshop)} className="group relative cursor-pointer">
              <div className="bg-white rounded-sm shadow-sm border border-stone-100 transition-all duration-700 flex flex-col md:flex-row min-h-[180px] overflow-hidden group-hover:shadow-2xl group-hover:-translate-y-1">
                 {/* 左侧色块 */}
                 <div className={`w-full h-1.5 md:w-2 md:h-auto ${workshop.colorClass} opacity-80 transition-opacity group-hover:opacity-100`}></div>
                 
                 {/* 中间主体内容 */}
                 <div className="flex-grow p-6 md:p-8 flex flex-col justify-center">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`text-[10px] font-mono text-stone-400 tracking-[0.4em] uppercase font-black`}>{workshop.engLabel}</span>
                    </div>
                    <h3 className="font-serif text-2xl text-stone-900 font-black mb-4 group-hover:text-stone-800 transition-colors">{workshop.title}</h3>
                    
                    <div className="flex items-center gap-2 text-stone-500 mb-4 bg-stone-50/80 self-start px-3 py-1 rounded-sm border border-stone-100/50">
                      <CalendarDays size={14} className={workshop.textClass} />
                      <span className="text-xs font-serif font-bold tracking-wide">{workshop.date}</span>
                    </div>

                    {/* 
                       调整：工坊摘要
                       - text-[15px] md:text-base -> text-base md:text-lg (加大 10% 左右)
                    */}
                    <p className="text-base md:text-lg text-stone-700 font-serif leading-relaxed line-clamp-2 opacity-90 group-hover:opacity-100">
                      {workshop.summary}
                    </p>
                 </div>

                 {/* 右侧副券 */}
                 <div className={`w-full md:w-[120px] ${workshop.accentBg} border-t md:border-t-0 md:border-l border-dashed border-stone-200 flex flex-row md:flex-col items-center justify-center p-4 md:p-0 relative group-hover:brightness-95 transition-all`}>
                    <div 
                      className={`md:[writing-mode:vertical-rl] whitespace-nowrap text-sm md:text-base tracking-[0.6em] font-serif font-black uppercase transition-all duration-500 ${workshop.status === 'full' ? 'text-stone-400' : workshop.textClass}`}
                    >
                      {workshop.statusLabel}
                    </div>
                    <div className="absolute -top-2 left-[-8px] w-4 h-4 bg-[#fafaf9] rounded-full hidden md:block border-b border-stone-100"></div>
                    <div className="absolute -bottom-2 left-[-8px] w-4 h-4 bg-[#fafaf9] rounded-full hidden md:block border-t border-stone-100"></div>
                 </div>
              </div>
            </div>
          ))}
        </div>
      </SectionWrapper>

      {/* 将弹窗移动到 SectionWrapper 外部 */}
      {activeWorkshop && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8 bg-stone-900/20 backdrop-blur-sm animate-fade-in" onClick={() => setActiveWorkshop(null)}>
           <div className="bg-[#fffefc] w-full max-w-xl max-h-[85vh] overflow-y-auto rounded-sm shadow-2xl relative flex flex-col" onClick={e => e.stopPropagation()}>
              
              <div className="p-8 pb-4 flex justify-between items-start">
                 <div>
                   <span className={`text-[10px] font-mono tracking-widest uppercase mb-1 block ${activeWorkshop.textClass}`}>{activeWorkshop.engLabel}</span>
                   <h2 className="font-serif text-2xl md:text-3xl text-stone-900 font-black">{activeWorkshop.title}</h2>
                 </div>
                 <button onClick={() => setActiveWorkshop(null)} className="text-stone-300 hover:text-stone-900 transition-colors p-2"><X size={24} /></button>
              </div>
              
              <div className="p-8 pt-2 space-y-10">
                 <div className="flex items-center gap-3 text-stone-800 font-serif text-sm bg-stone-50 p-4 border-l-4 border-stone-200">
                   <Clock3 size={16} className={activeWorkshop.textClass} />
                   <span className="font-bold">活动时间：{activeWorkshop.date}</span>
                 </div>

                 <div className="prose prose-stone max-w-none font-serif text-stone-700 leading-loose text-[15px] whitespace-pre-wrap">
                    {activeWorkshop.details}
                 </div>
                 
                 <div className="pt-8 border-t border-stone-100 flex flex-col items-center">
                    <img 
                      src={ASSETS.workshopQR} 
                      alt="活动报名二维码" 
                      className="w-24 h-24 object-contain mb-4 opacity-80"
                    />
                    <div className="text-center font-serif text-sm">
                      <p className="text-stone-600">
                        报名方式：请<span className="text-stone-900 font-bold underline decoration-stone-200 underline-offset-4">扫描二维码</span>进行报名
                      </p>
                      <p className="text-stone-300 font-mono text-[9px] tracking-[0.2em] uppercase mt-2">
                         Contact Assistant for Registration
                      </p>
                    </div>
                 </div>
              </div>
           </div>
        </div>
      )}
    </>
  );
};
